import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, MapPin, Phone, Send, Linkedin, Github, Twitter, MessageCircle, CheckCircle, AlertCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { AnimatePresence } from 'framer-motion';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // DEBUG: Check environment variables on mount
  useEffect(() => {
    console.group('🔍 EmailJS Debug - Environment Check');
    console.log('Service ID exists:', !!process.env.REACT_APP_EMAILJS_SERVICE_ID);
    console.log('Template ID exists:', !!process.env.REACT_APP_EMAILJS_TEMPLATE_ID);
    console.log('Public Key exists:', !!process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
    console.log('Service ID:', process.env.REACT_APP_EMAILJS_SERVICE_ID);
    console.log('Template ID:', process.env.REACT_APP_EMAILJS_TEMPLATE_ID);
    console.log('Public Key (first 10 chars):', process.env.REACT_APP_EMAILJS_PUBLIC_KEY?.substring(0, 10));
    console.groupEnd();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    };
    return now.toLocaleDateString('en-US', options);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    console.group('📧 EmailJS Debug - Sending Email');
    console.log('Timestamp:', new Date().toISOString());
    
    try {
      // DEBUG: Log configuration
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      console.log('Configuration Check:');
      console.log('- Service ID:', serviceId || '❌ MISSING');
      console.log('- Template ID:', templateId || '❌ MISSING');
      console.log('- Public Key:', publicKey ? '✅ Present' : '❌ MISSING');

      // DEBUG: Log form data
      console.log('Form Data:');
      console.log('- Name:', formData.name);
      console.log('- Email:', formData.email);
      console.log('- Message length:', formData.message.length);

      // DEBUG: Log template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        time: getCurrentDateTime(),
      };
      console.log('Template Parameters:', templateParams);

      // DEBUG: Check EmailJS library
      console.log('EmailJS library loaded:', typeof emailjs);
      console.log('EmailJS.send exists:', typeof emailjs.send === 'function');

      // Attempt to send
      console.log('🚀 Attempting to send email...');
      const result = await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      console.log('✅ SUCCESS! Email sent');
      console.log('Response:', result);
      console.log('Status:', result.status);
      console.log('Text:', result.text);

      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('❌ ERROR! Email failed to send');
      console.error('Error object:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message || error.text);
      console.error('Error status:', error.status);
      console.error('Full error:', JSON.stringify(error, null, 2));
      
      // DEBUG: Additional error details
      if (error.text) {
        console.error('EmailJS error text:', error.text);
      }
      if (error.status) {
        console.error('HTTP Status:', error.status);
        switch(error.status) {
          case 400:
            console.error('💡 Bad Request - Check template parameters match your EmailJS template');
            break;
          case 401:
            console.error('💡 Unauthorized - Check your Public Key');
            break;
          case 404:
            console.error('💡 Not Found - Check Service ID and Template ID');
            break;
          case 412:
            console.error('💡 Precondition Failed - Check EmailJS account/service status');
            break;
          case 422:
            console.error('💡 Invalid parameters - Template variable mismatch');
            break;
          default:
            console.error('💡 Unknown error - Check EmailJS dashboard and logs');
        }
      }

      // DEBUG: Network check
      console.log('Network status:', navigator.onLine ? '✅ Online' : '❌ Offline');
      
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      console.groupEnd();
    }
  };

  const socialLinks = [
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/in/dev-bhushan-bhatta-a9bb4133b/',
      icon: Linkedin,
      color: 'hover:text-blue-400'
    },
    {
      name: 'GitHub',
      url: 'https://github.com/Bhushan791',
      icon: Github,
      color: 'hover:text-gray-300'
    },
    {
      name: 'Twitter',
      url: 'https://x.com/Bhushan_Bhatta',
      icon: Twitter,
      color: 'hover:text-blue-400'
    }
  ];

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'bhushanbhatta777@gmail.com',
      href: 'mailto:bhushanbhatta777@gmail.com'
    },
    {
      icon: MapPin,
      label: 'Location',
      value: 'Nepal',
      href: null
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+977 9748857597',
      href: 'tel:+9779748857597'
    }
  ];

  return (
    <section id="contact" className="py-20 px-4 relative">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-primary-gold/5 to-primary-violet/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="section-title gradient-text">Get In Touch</h2>
          <p className="section-subtitle">Let's discuss your next project or opportunity</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="glass-effect p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-primary-text mb-6">Let's Connect</h3>
              <p className="text-primary-muted mb-8 leading-relaxed">
                I'm always open to discussing new opportunities, interesting projects, 
                or just having a chat about technology and development. Feel free to reach out!
              </p>

              {/* Contact Details */}
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className="flex items-center gap-4 group"
                  >
                    <div className="p-3 bg-primary-gold/10 rounded-xl group-hover:bg-primary-gold/20 transition-colors">
                      <info.icon className="w-6 h-6 text-primary-gold" />
                    </div>
                    <div>
                      <p className="text-sm text-primary-muted font-medium">{info.label}</p>
                      {info.href ? (
                        <a
                          href={info.href}
                          className="text-primary-text hover:text-gradient transition-colors font-medium"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-primary-text font-medium">{info.value}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Social Links */}
              <div className="mt-8 pt-8 border-t border-white/10">
                <h4 className="text-lg font-semibold text-primary-text mb-4">Follow Me</h4>
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={inView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                      whileHover={{ scale: 1.1, y: -3 }}
                      whileTap={{ scale: 0.9 }}
                      className={`p-3 bg-white/5 rounded-xl border border-white/10 social-icon ${social.color} transition-all duration-300`}
                    >
                      <social.icon className="w-6 h-6" />
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>

            {/* Availability Status */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="glass-effect p-6 rounded-2xl"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-medium">Available for opportunities</span>
              </div>
              <p className="text-primary-muted text-sm">
                I'm currently open to freelance work, full-time positions, and exciting collaborations.
              </p>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="glass-effect p-8 rounded-2xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle className="w-6 h-6 text-primary-gold" />
              <h3 className="text-2xl font-bold text-primary-text">Send Message</h3>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-primary-muted mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="form-input w-full"
                  placeholder="Your full name"
                />
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-primary-muted mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="form-input w-full"
                  placeholder="your_email@example.com"
                />
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-primary-muted mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="form-input w-full resize-none"
                  placeholder="Drop me a message..."
                />
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner w-5 h-5"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </motion.button>

              {/* Status Messages */}
              <AnimatePresence>
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-xl"
                  >
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-medium">Message sent successfully!</span>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl"
                  >
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <div>
                      <span className="text-red-400 font-medium">Failed to send message. Please try again.</span>
                      <p className="text-red-300 text-xs mt-1">Check console for detailed error logs</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>

            {/* Form Note */}
            <p className="text-xs text-primary-muted mt-6 text-center">
              I typically respond within 24 hours. For urgent matters, feel free to reach out via email or phone.
            </p>
          </motion.div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
          className="text-center mt-16"
        >
          <div className="glass-effect p-8 rounded-2xl max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-primary-text mb-4">
              Ready to work together?
            </h3>
            <p className="text-primary-muted mb-6">
              Let's discuss how I can help bring your ideas to life with cutting-edge technology and creative solutions.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="btn-primary flex items-center gap-2 mx-auto"
            >
              <Mail className="w-5 h-5" />
              Start a Conversation
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;